/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './OrganizationChart';

const OrganizationChartManager: ComponentManagerFn<'OrganizationChart'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'OrganizationChart'>(platform, 'OrganizationChart'),
    type: 'OrganizationChart',
    name: 'Organization Chart',
    isChildAllowed: (page, item, childType) =>
      (['Box', 'Card'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Primary Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Choose an attribute that should contain the primary node value.',
      },
      parentAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Parent Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'Choose an attribute that should contain the parent node value.',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText: 'Provide width for the chart.',
      },
      nodeWidth: {
        label: 'Node Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText:
          'Provide a numeric value to set the width of the node in pixels.',
      },
      margin: {
        label: 'Node margin',
        type: 'string',
        helpText:
          'Specify a margin value (in pixels) to apply spacing around the node.',
      },
      pinRootNode: {
        label: 'Pin Root Node',
        type: 'boolean',
        helpText:
          'When enabled, the root node stays visible while you scroll horizontally through the chart.',
      },
      groupLeafNodes: {
        label: 'Group Leaf Nodes',
        type: 'boolean',
        helpText:
          'When enabled, nodes in the last layer (leaf nodes) are grouped together with a wrapping layout.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'keyAttribute':
        case 'parentAttribute':
          return !!itemData.dsalias;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    category: 'Forms',
    icon: { iconName: 'database' },
    description: `**Overview**<br/>
The Organization Chart component is a data visualization tool designed to display hierarchical relationships. While most commonly used to show employee reporting structures (Org Charts), it can be used for any parent-child dataset, such as Category Trees, Family Trees, or Network Topologies.
It takes a flat list of records and dynamically reconstructs the visual tree structure based on ID relationships.

**1. Data Binding (Defining the Hierarchy)**<br/>
The component works with **Flat Data (Adjacency List model)**. You do not need nested JSON; you just need a standard database table where each row points to its parent.
* **DataSource Alias:** Select the Data Store containing the records (e.g., "Employees").
* **Primary Attribute (ID):** The unique identifier for the node (e.g., "Employee_ID").
* **Parent Attribute (Manager):** The identifier of the parent node (e.g., "Manager_ID").
* **Logic:** The component links these two fields to draw the lines. If a record has no Parent ID (null), it becomes the Root Node at the top of the chart.



**2. Designing the Node (The Template)**<br/>
The Organization Chart handles the structure (the lines and positioning), but you define what each card looks like.
* **Allowed Children:** You must add a Box or Card component as a child.
* **Template Behavior:** The child Box acts as a template. You design it once (e.g., adding a UserPic, a Text for Name, and a Text for Job Title), and the chart repeats this layout for every node in the tree, populating it with that specific record's data.

**3. Layout & Dimensions**<br/>
* **Chart Height / Width:** Sets the overall canvas size for the chart area.
* **Node Width:** Sets the fixed width (in pixels) for every individual node card.
* **Node Margin:** Controls the spacing between nodes.

**4. Advanced Visualization**<br/>
* **Pin Root Node:**
    * **Enabled:** If the chart is very wide (many branches), scrolling horizontally keeps the top-level Root Node fixed in view, so users don't lose context of who leads the tree.
* **Group Leaf Nodes:**
    * **Enabled:** Optimizes space for the bottom-most level (Leaf Nodes). Instead of spreading them out horizontally in a massive line, it groups them vertically or in a wrapped layout under their parent. This significantly reduces the total width of the chart.`,
  };
};

export default OrganizationChartManager;
